import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	type Invoice,
	type InvoiceLineItemSummary,
	InvoiceStatus,
	type PaginationRequest,
} from "@pilatestime/shared-types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Eye, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	fetchCustomerInvoicesQueryOptions,
	useCancelInvoice,
} from "@/api/billing";
import { CurrencyDisplay, InvoiceStatusBadge } from "@/components/billing";
import { ConfirmationWarningModal } from "@/components/ConfirmationWarningModal";
import type { PaginationMeta } from "@/components/data-table";
import { DataTable, useDataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";

// Search params schema
const customerBillingSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId/billing",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return {
			userId,
			token,
			orgId: params.orgId,
			customerId: params.customerId,
		};
	},
	validateSearch: customerBillingSearchSchema,
	component: CustomerBillingPage,
});

// Extended invoice type with customer info from API response
type InvoiceRow = Invoice & {
	lineItemCount?: number;
	lineItems?: InvoiceLineItemSummary[];
	paidAmount?: string;
};

const CANCELLABLE_INVOICE_STATUSES: InvoiceStatus[] = [
	InvoiceStatus.DRAFT,
	InvoiceStatus.OPEN,
	InvoiceStatus.PARTIALLY_PAID,
	InvoiceStatus.OVERDUE,
];

const canCancelInvoice = (status: InvoiceStatus) =>
	CANCELLABLE_INVOICE_STATUSES.includes(status);

// Helper to format line items summary
const formatLineItemsSummary = (lineItems?: InvoiceLineItemSummary[]) => {
	if (!lineItems || lineItems.length === 0) {
		return { summary: "—", tooltip: "" };
	}

	// Create summary: first item description + count if more
	const firstItem = lineItems[0];
	const summary =
		lineItems.length === 1
			? firstItem.description
			: `${firstItem.description} +${lineItems.length - 1}`;

	// Create tooltip with all items
	const tooltip = lineItems
		.map((item) => `${item.description} (${item.quantity}x)`)
		.join("\n");

	return { summary, tooltip };
};

// Custom query options wrapper that matches useDataTable's expected signature
const customerInvoicesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	pagination: PaginationRequest,
	searchTerm?: string,
	customerId?: string,
) =>
	fetchCustomerInvoicesQueryOptions(
		userId,
		token,
		organizationId,
		customerId ?? "",
		{
			...pagination,
			search: searchTerm,
		},
	);

interface CustomerInvoiceListProps {
	organizationId: string;
	customerId: string;
	pagination: PaginationRequest;
	searchTerm?: string;
	userId: string;
	token: string;
	canManageBilling: boolean;
	onPaginationMetaChange?: (meta: PaginationMeta) => void;
	onViewInvoice?: (invoiceId: string) => void;
}

function CustomerInvoiceList({
	organizationId,
	customerId,
	pagination,
	searchTerm,
	userId,
	token,
	canManageBilling,
	onPaginationMetaChange,
	onViewInvoice,
}: CustomerInvoiceListProps) {
	const { t } = useTranslation();
	const { formatDate } = useDateFormatter();
	const cancelInvoiceMutation = useCancelInvoice();
	const [invoiceToCancel, setInvoiceToCancel] = useState<InvoiceRow | null>(
		null,
	);

	const handleOpenCancelModal = useCallback((invoice: InvoiceRow) => {
		setInvoiceToCancel(invoice);
	}, []);

	const handleCloseCancelModal = useCallback(() => {
		setInvoiceToCancel(null);
	}, []);

	const handleConfirmCancel = useCallback(async () => {
		if (!invoiceToCancel) return;

		await cancelInvoiceMutation.mutateAsync({
			token,
			organizationId,
			invoiceId: invoiceToCancel.id,
		});

		setInvoiceToCancel(null);
	}, [cancelInvoiceMutation, token, organizationId, invoiceToCancel]);

	const {
		data: invoices,
		isLoading,
		error,
		paginationMeta,
	} = useDataTable<InvoiceRow, string>({
		queryOptions: customerInvoicesQueryOptions,
		userId,
		token,
		organizationId,
		pagination,
		searchTerm,
		additionalParams: customerId,
	});

	// Track previous pagination meta to avoid infinite loops
	const prevMetaRef = useRef<string>("");
	const metaKey = `${paginationMeta.page}-${paginationMeta.limit}-${paginationMeta.total}-${paginationMeta.totalPages}`;

	// Notify parent of pagination changes (only when values actually change)
	useEffect(() => {
		if (metaKey !== prevMetaRef.current) {
			prevMetaRef.current = metaKey;
			onPaginationMetaChange?.(paginationMeta);
		}
	}, [metaKey, paginationMeta, onPaginationMetaChange]);

	const cancelInfoItems = useMemo(() => {
		if (!invoiceToCancel) return [];

		const items = [
			{
				label: t("billing.invoices.invoiceNumber"),
				value: invoiceToCancel.id,
			},
		];

		if (invoiceToCancel.lineItems && invoiceToCancel.lineItems.length > 0) {
			const lineItemsValue = invoiceToCancel.lineItems
				.map((item) => `${item.description} (${item.quantity}x)`)
				.join(", ");

			items.push({
				label: t("billing.invoices.lineItems"),
				value: lineItemsValue,
			});
		}

		return items;
	}, [invoiceToCancel, t]);

	const handleView = useCallback(
		(invoiceId: string) => {
			onViewInvoice?.(invoiceId);
		},
		[onViewInvoice],
	);

	const columns: ColumnDef<InvoiceRow>[] = useMemo(() => {
		const cols: ColumnDef<InvoiceRow>[] = [
			{
				id: "lineItems",
				header: t("billing.invoices.lineItems"),
				cell: ({ row }) => {
					const { summary, tooltip } = formatLineItemsSummary(
						row.original.lineItems,
					);
					if (!tooltip) {
						return <span className="text-base-content/50">{summary}</span>;
					}
					return (
						<Tooltip>
							<TooltipTrigger asChild>
								<span className="truncate max-w-[200px] block cursor-help">
									{summary}
								</span>
							</TooltipTrigger>
							<TooltipContent className="whitespace-pre-line">
								{tooltip}
							</TooltipContent>
						</Tooltip>
					);
				},
			},
			{
				accessorKey: "totalAmount",
				header: t("billing.invoices.amount"),
				cell: ({ row }) => (
					<CurrencyDisplay amount={row.original.totalAmount} />
				),
			},
			{
				accessorKey: "status",
				header: t("billing.invoices.status"),
				cell: ({ row }) => <InvoiceStatusBadge status={row.original.status} />,
			},
			{
				accessorKey: "dueDate",
				header: t("billing.invoices.dueDate"),
				cell: ({ row }) => {
					const dueDate = row.original.dueDate;
					if (!dueDate) return <span className="text-base-content/50">—</span>;
					return <span>{formatDate(new Date(dueDate).getTime())}</span>;
				},
			},
		];

		// Add actions column
		cols.push({
			id: "actions",
			header: t("common.actions"),
			cell: ({ row }) => (
				<div className="flex items-center space-x-2">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => handleView(row.original.id)}
						data-testid="invoice-view-button"
					>
						<Eye className="w-4 h-4" />
					</Button>
					{canManageBilling && canCancelInvoice(row.original.status) && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => handleOpenCancelModal(row.original)}
							data-testid="invoice-cancel-button"
							disabled={
								cancelInvoiceMutation.isPending &&
								invoiceToCancel?.id === row.original.id
							}
							title={t("billing.invoices.actions.cancel")}
						>
							<X className="text-error hover:text-error w-4 h-4" />
						</Button>
					)}
				</div>
			),
		});

		return cols;
	}, [
		t,
		formatDate,
		handleView,
		canManageBilling,
		cancelInvoiceMutation,
		invoiceToCancel,
		handleOpenCancelModal,
	]);

	return (
		<>
			<DataTable
				data={invoices}
				isLoading={isLoading}
				error={error}
				columns={columns}
				testId="customer-invoices"
				canManage={canManageBilling}
				emptyState={{
					title: searchTerm
						? t("common.noSearchResults")
						: t("billing.invoices.noInvoices"),
				}}
			/>
			{invoiceToCancel && (
				<ConfirmationWarningModal
					isOpen={!!invoiceToCancel}
					onClose={handleCloseCancelModal}
					onConfirm={handleConfirmCancel}
					title={t("billing.invoices.cancelDialog.title")}
					subtitle={t("billing.invoices.cancelDialog.description", {
						invoiceNumber: invoiceToCancel.id,
					})}
					infoSectionTitle={t("billing.invoices.cancelDialog.invoiceInfoTitle")}
					infoItems={cancelInfoItems}
					confirmButtonText={t("billing.invoices.cancelDialog.confirm")}
					loadingText={t("billing.invoices.cancelDialog.loading")}
					isLoading={cancelInvoiceMutation.isPending}
					variant="danger"
				/>
			)}
		</>
	);
}

function CustomerBillingPage() {
	const { t } = useTranslation();
	const { userId, token, orgId, customerId } = Route.useLoaderData();
	const search = Route.useSearch();
	const navigate = useNavigate();

	// Access control
	const can = useCan();
	const canManageBilling = can(Action.MANAGE_PAYMENTS);

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState<PaginationMeta>({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Navigate to invoice details page
	const handleViewInvoice = useCallback(
		(invoiceId: string) => {
			navigate({
				to: "/app/org/$orgId/billing/$invoiceId/overview",
				params: { orgId, invoiceId },
			});
		},
		[navigate, orgId],
	);

	// Debounce search term
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1);
	};

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	return (
		<div className="h-full flex flex-col overflow-hidden space-y-4">
			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="flex-shrink-0 pb-4">
					<div className="flex flex-col sm:flex-row gap-4">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/50" />
							<Input
								placeholder={t("common.search")}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="pl-10"
								data-testid="customer-billing-search-input"
							/>
						</div>
					</div>
				</CardHeader>
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
					<div className="flex-shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
					<div className="flex-1 overflow-hidden">
						<CustomerInvoiceList
							organizationId={orgId}
							customerId={customerId}
							pagination={{ page: currentPage, limit: pageSize }}
							searchTerm={debouncedSearchTerm}
							userId={userId}
							token={token}
							canManageBilling={canManageBilling}
							onPaginationMetaChange={handlePaginationMetaChange}
							onViewInvoice={handleViewInvoice}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
